interface Cabin {
  name: string;
  type: string;
  location: string;
  description: string;
  highlights: string[];
  image: string;
  imageAlt: string;
  airbnbUrl: string;
  petFriendly: boolean;
  nearWaterfall: boolean;
}

const CABINS: Cabin[] = [
  {
    name: 'Glen Falls Luxury Cabin',
    type: 'Luxury Cabin',
    location: 'Highlands, NC — Near Glen Falls',
    description: 'Glen Falls Luxury Cabin in Highlands, NC is a highly rated, pet-friendly retreat located just minutes from Glen Falls and close to many of the area\'s most popular hiking trails and waterfalls. With its peaceful wooded setting and thoughtful design, it offers a comfortable and convenient stay for guests traveling with dogs.',
    highlights: [
      'Consistently rated as a Guest Favorite on Airbnb',
      'One of the most highly rated pet-friendly cabins in Highlands, NC',
    ],
    image: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/ba0d51886e955561a1eff3aa82f68703.jpeg',
    imageAlt: 'Pet-friendly cabin in Highlands North Carolina near Glen Falls waterfall',
    airbnbUrl: 'https://www.airbnb.com/rooms/900012473285450950',
    petFriendly: true,
    nearWaterfall: true,
  },
  {
    name: 'Lark Lodge',
    type: 'Character Cabin',
    location: 'Highlands, NC',
    description: 'A pet-friendly cabin in Highlands featuring a covered front porch, stone fireplace, and screened side porch set within a quiet wooded area. Comfortably accommodates up to six guests and is well-suited for travelers looking for easy access to downtown Highlands and the surrounding area.',
    highlights: [
      'Pet friendly',
      'Covered front porch and screened side porch',
    ],
    image: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/aaecc6fc9d1d9ac8bd4441230fe5683d.jpeg',
    imageAlt: 'Dog-friendly cabin in Highlands North Carolina with covered porch and wooded setting',
    airbnbUrl: 'https://www.airbnb.com/rooms/1617475116852690845',
    petFriendly: true,
    nearWaterfall: false,
  },
  {
    name: 'Stag Ridge',
    type: 'Luxury Forest Cabin',
    location: 'Highlands, NC — Wooded Ridge',
    description: 'A spacious pet-friendly cabin in Highlands offering a wraparound deck, indoor stone fireplace, and full kitchen set within a tranquil wooded ridge. Accommodates up to six guests and provides easy access to hiking trails and downtown Highlands.',
    highlights: [
      'Pet friendly',
      'Wraparound deck with forest views',
    ],
    image: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/46fa8df15f4549c7bb8d2bc9e3c35336.jpeg',
    imageAlt: 'Luxury mountain cabin in Highlands North Carolina with wraparound deck and wooded forest setting',
    airbnbUrl: 'https://www.airbnb.com/rooms/1180227781183226897',
    petFriendly: true,
    nearWaterfall: false,
  },
];

function CabinCard({ cabin }: { cabin: Cabin }) {
  return (
    <article className="bg-white rounded-2xl overflow-hidden flex flex-col lg:flex-row">
      <a
        href={cabin.airbnbUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="lg:w-[40%] h-64 lg:h-auto overflow-hidden shrink-0 block group cursor-pointer"
      >
        <img
          src={cabin.image}
          alt={cabin.imageAlt}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </a>
      <div className="flex-1 p-8 flex flex-col justify-between">
        <div>
          <div className="mb-4">
            <span className="text-[#6B8C7D] text-xs tracking-widest uppercase font-jost block mb-1">{cabin.type}</span>
            <a
              href={cabin.airbnbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer hover:text-[#2D4A3E] transition-colors"
            >
              <h3 className="font-cormorant text-3xl text-[#1A1A18] font-medium leading-tight">{cabin.name}</h3>
            </a>
            <p className="text-[#9C9890] text-xs font-jost mt-1">{cabin.location}</p>
          </div>
          <p className="text-[#5C5850] text-sm font-jost leading-relaxed mb-5">{cabin.description}</p>
          <ul className="space-y-1.5 mb-5">
            {cabin.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-[#5C5850] text-xs font-jost">
                <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-check-line text-[#6B8C7D] text-xs" />
                </div>
                {h}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-between pt-5 border-t border-[#F5F1EB]">
          <div className="flex items-center gap-2">
            {cabin.petFriendly && (
              <span className="flex items-center gap-1.5 text-[#6B8C7D] text-xs font-jost bg-[#EEF5EE] px-3 py-1.5 rounded-full">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-paw-print-line text-xs" />
                </div>
                Pet Friendly
              </span>
            )}
            {cabin.nearWaterfall && (
              <span className="flex items-center gap-1.5 text-[#3A6A8C] text-xs font-jost bg-[#EEF2F8] px-3 py-1.5 rounded-full">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-water-flash-line text-xs" />
                </div>
                Near Falls
              </span>
            )}
          </div>
          <a
            href={cabin.airbnbUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-[#2D4A3E] text-white text-xs font-jost tracking-[0.12em] uppercase px-5 py-2.5 rounded-full cursor-pointer hover:bg-[#1e3329] transition-colors duration-200"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-external-link-line text-xs" />
            </div>
            View on Airbnb
          </a>
        </div>
      </div>
    </article>
  );
}

export default function FeaturedCabinsSection() {
  return (
    <section className="bg-[#FAFAF8] py-24 px-6 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost mb-4">/ Curated Selection</p>
          <h2 className="font-cormorant text-5xl text-[#1A1A18] font-light mb-4">
            Featured Pet-Friendly Cabins
          </h2>
          <p className="text-[#5C5850] font-jost text-sm max-w-xl mx-auto leading-relaxed">
            A manually selected list of pet-friendly cabin options in Highlands, NC — each verified as welcoming to dogs.
          </p>
        </div>
        <div className="space-y-6">
          {CABINS.map((cabin) => (
            <CabinCard key={cabin.name} cabin={cabin} />
          ))}
        </div>
      </div>
    </section>
  );
}
